import { useEventNameMatch } from '@shared/components/AuthProvider/AuthProvider.utils';
import { get } from 'lodash-es';
import {
  EventRSVPAttendanceStatus,
  EventScheduleItemStreamFragment,
  useGetEventCTAButtonsDataQuery,
  useGuestCanGetAccessEventByNameActionBarQuery,
  useGetEventCTAButtonsDataLazyQuery,
} from '@graphql/generated';
import { useUnlockDialogContext } from '@apps/guest/packages/layout-engine/components/UnlockDialog/UnlockDialogProvider';
import { performFullPageRedirect } from '@shared/utils/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOnIntersectingChange } from '@shared/utils/hooks/useOnIntersectingChange';
import { isStartMinutesBefore } from '@apps/guest/packages/layout-engine/components/VirtualEventBlock/VirtualEventBlock.utils';
import { isPast } from 'date-fns';
import { useActionBarTelemetry } from './ActionBar.telemetry';
import { useCtaButtons } from '@apps/guest/packages/layout-engine/common/utils/useCtaButtons';
import { CtaButtonsSessionData } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons.types';
import { withWindow } from '@shared/utils/withWindow';
import { useRouteMatch } from '@react-router';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFormatEventDate } from '@localization/hooks/useFormatEventDate';
import { useGuestGuideVisibility } from './useGuestGuideVisibility';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useLayout } from '@shared/context/LayoutProvider';

export const useActionBarController = () => {
  const { handleUnlockDialogOpen } = useUnlockDialogContext();
  const [isTriggerPoint, setIsTriggerPoint] = useState(false);
  const triggerPointRef = useRef(null);
  const nameMatchResult = useEventNameMatch();
  const eventHandle = get(nameMatchResult, 'eventName', '');
  const { eventInfo } = useEventInfo();
  const [isMobile] = useResponsive({
    values: {
      xxs: true,
      xs: false,
    },
  });
  const eventDate = useFormatEventDate(eventInfo, 'MMMM do, yyyy');

  const { data } = useGuestCanGetAccessEventByNameActionBarQuery({
    variables: { eventHandle },
    batchMode: 'fast',
    ssr: false,
  });
  // Marking this query as `ssr: false` because any RSVP data query is proxied through app-server-api -- which takes long to resolve.
  const { data: sessionData } = useGetEventCTAButtonsDataQuery({
    variables: { name: eventHandle || '' },
    batchMode: 'fast',
    ssr: false,
  });

  const [getCtaData, { data: guestGuideSessionData, called: calledCTA, loading: loadingCTA }] = useGetEventCTAButtonsDataLazyQuery({
    batchMode: 'fast',
    ssr: false,
  });

  useEffect(() => {
    if (eventHandle) {
      getCtaData({
        variables: { name: eventHandle },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);

  const { getButtonsVisibility, getUnlockButtonText, getRSVPButtonText } = useCtaButtons();
  const nowDate = Date.now();
  const telemetry = useActionBarTelemetry();

  //Livestream variables parsing
  const streamNow = get(data, 'eventByName.info.schedule.virtualScheduleItemNow', null);
  const scheduleItems = get(data, 'eventByName.info.schedule.items', []);

  const streamToDisplay =
    streamNow ||
    scheduleItems.filter((item: EventScheduleItemStreamFragment) => !!item.virtualEventLink && isStartMinutesBefore(item.startTime?.milliseconds || 0, nowDate, 15))[0];

  const streamEvent = get(streamToDisplay, 'virtualEventLink', null);
  const streamStartTimeMs = get(streamToDisplay, 'startTime.milliseconds', 0);
  const streamEndTimeMs = get(streamToDisplay, 'endTime.milliseconds', 0);
  const resolvedEndTimeMs = streamEndTimeMs || streamStartTimeMs + 1000 * 60 * 60;
  const isEventPast = isPast(resolvedEndTimeMs);

  //Rest Event variables parsing
  const hasUnlocked = sessionData?.eventByName?.activeSession.hasUnlocked ?? false;
  const rsvpIsEnabled = sessionData?.eventByName?.activeSession.eventCTA.rsvpIsEnabled ?? false;
  const rsvpAttendanceStatus = sessionData?.eventByName?.activeSession?.eventCTA?.rsvpAttendanceStatus || EventRSVPAttendanceStatus.notAttending;
  const scheduleHasInviteOnlyItems = sessionData?.eventByName?.activeSession.eventCTA.scheduleHasInviteOnlyItems ?? false;
  const rsvpLabel = sessionData?.eventByName?.activeSession.eventCTA.label ?? '';

  const onVirtualEventBlockClicked = useCallback(() => {
    telemetry.onVirtualEventClicked();
    if (!streamEvent) return;
    const { urlLinkText, isLinkValid } = streamEvent;
    if (!isLinkValid || !urlLinkText) return;
    withWindow(global => global.open?.(urlLinkText, '_blank'));
  }, [streamEvent, telemetry]);

  const onRsvpHandle = () => {
    performFullPageRedirect(`/${eventHandle}/rsvp`);
    telemetry.onRsvpHandle();
  };

  const onIntersectingTriggerPointEl = useCallback(
    (isIntersecting: boolean) => {
      if (isIntersecting && triggerPointRef.current) {
        setIsTriggerPoint(true);
      } else {
        setIsTriggerPoint(false);
      }
    },
    [setIsTriggerPoint],
  );

  useOnIntersectingChange(triggerPointRef, onIntersectingTriggerPointEl);

  //General conditions
  const isStreamDisplayed = !!streamToDisplay && !isEventPast;
  const event: CtaButtonsSessionData = sessionData?.eventByName ?? {};
  const { welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, customPages } = event;
  const pages = [welcomePage, storyPage, tidbitsPage, schedulePage, travelPage, faqPage, weddingPage, registryPage, momentsPage, rsvpPage, ...(customPages ?? [])];
  const rsvpIsPasswordProtected = rsvpPage?.private;
  const { shouldDisplayUnlockButton, shouldDisplayRSVPButton } = getButtonsVisibility(
    pages,
    welcomePage,
    schedulePage,
    rsvpIsEnabled,
    rsvpIsPasswordProtected,
    scheduleHasInviteOnlyItems,
    hasUnlocked,
    customPages,
  );
  const unlockButtonText = getUnlockButtonText(hasUnlocked);
  const rsvpButtonText = getRSVPButtonText(rsvpAttendanceStatus, rsvpLabel);

  //Display on Brannan Welcome only when Live
  const { layout } = useLayout();
  const isWelcomePage = !!useRouteMatch('/:eventHandle/welcome');
  const isNotLiveOnBrannanWelcome = layout === 'brannan' && isWelcomePage && !streamNow;

  // Hide event date on mobile when unlock button is shown to save space
  const showEventDate = useMemo(() => {
    if (!eventDate) {
      return false;
    }
    return shouldDisplayUnlockButton && isMobile ? false : eventDate;
  }, [eventDate, shouldDisplayUnlockButton, isMobile]);

  const isGuestGuideShown = useGuestGuideVisibility({
    sessionData: guestGuideSessionData,
    ctaQueryCalled: calledCTA,
    ctaQueryLoading: loadingCTA,
    shouldDisplayUnlockButton: shouldDisplayUnlockButton ?? false,
  });
  const [isGuestGuideExperimentEnabled] = useIsFeatureEnabled({
    consentKey: 'guestguideexperiment',
    skip: !isGuestGuideShown || isStreamDisplayed,
  });

  return {
    isActionBarHidden: (!shouldDisplayUnlockButton && !shouldDisplayRSVPButton && !isStreamDisplayed) || isNotLiveOnBrannanWelcome,

    isTriggerPoint,
    triggerPointRef,
    shouldDisplayRSVPButton,
    onUnlockDialogHandle: () => handleUnlockDialogOpen(),
    shouldDisplayUnlockButton,
    rsvpButtonText,
    onRsvpHandle,
    isStreamDisplayed,
    streamEvent,
    onVirtualEventBlockClicked,
    unlockButtonText,
    showEventDate,
    isGuestGuideExperimentEnabled,
    guestGuideSessionData,
  };
};
